import { motion } from "framer-motion";
import { Wifi, Building2, Clock, ArrowUpRight } from "lucide-react";
import { INDIA_DEGREES, ONLINE_COURSES, EXTERNAL_LINKS } from "@/lib/site-data";

export default function IndiaEducation() {
  return (
    <section id="india" className="bg-sand/40 border-y border-border/60 py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mb-14"
        >
          <span className="text-xs uppercase tracking-[0.3em] text-primary font-medium">
            Study in India — Online & Offline
          </span>
          <h2 className="font-display text-4xl md:text-6xl text-secondary mt-4 leading-[1.02]">
            World-class degrees, right here at home.
          </h2>
          <p className="text-foreground/70 mt-6 text-lg leading-relaxed">
            Admissions guidance for India's top universities — from B.Tech and MBA
            to M.Sc and Ph.D — across on-campus and online modes, plus career-focused
            certifications you can start today.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <span className="inline-flex items-center gap-2 rounded-full bg-card border border-border px-4 py-2 text-sm text-secondary">
              <Building2 size={15} className="text-primary" /> On-campus (Offline)
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-card border border-border px-4 py-2 text-sm text-secondary">
              <Wifi size={15} className="text-primary" /> Online / Distance
            </span>
          </div>
        </motion.div>

        {/* Degrees grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {INDIA_DEGREES.map((d, i) => (
            <motion.div
              key={d.code}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              data-testid={`india-degree-${i}`}
              className="group rounded-2xl border border-border bg-card p-7 hover:-translate-y-1 hover:border-primary/50 transition-[transform,border-color] duration-400"
            >
              <div className="font-display text-4xl text-primary/90 font-semibold">{d.code}</div>
              <h3 className="text-lg font-medium text-secondary mt-3">{d.name}</h3>
              <p className="text-sm text-foreground/65 mt-2 leading-relaxed">{d.note}</p>
            </motion.div>
          ))}
        </div>

        {/* Online courses */}
        <div className="flex items-end justify-between mb-8">
          <h3 className="font-display text-3xl text-secondary">Career-focused online courses</h3>
          <a
            href={EXTERNAL_LINKS.careerbanale}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="india-courses-cta"
            className="hidden sm:inline-flex items-center gap-2 text-secondary font-medium border-b-2 border-primary pb-1 hover:gap-3 transition-all"
          >
            View catalogue <ArrowUpRight size={16} className="text-primary" />
          </a>
        </div>
        <div className="rounded-2xl border border-border bg-card overflow-hidden divide-y divide-border">
          {ONLINE_COURSES.map((c, i) => (
            <motion.div
              key={c.name}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              data-testid={`online-course-${i}`}
              className="group flex items-center justify-between gap-4 px-6 py-5 hover:bg-sand/50 transition-colors"
            >
              <span className="text-secondary font-medium group-hover:text-primary transition-colors">{c.name}</span>
              <div className="flex items-center gap-6 shrink-0">
                <span className="hidden sm:flex items-center gap-1.5 text-sm text-foreground/60"><Clock size={14} /> {c.duration}</span>
                <span className="text-sm font-medium text-secondary w-20 text-right">{c.fee}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
